import type { CidadeLoja,
  AcessoEstado, Account, FounderConferido, FounderEstado, FounderPedido, ColetaEstado, Appearance, CharacterDetail, CharacterSummary,
  CreationCatalog, Credentials, CredencialGoogle, CodigoEmail, RedefinirSenha, Friend, FriendLookup,   GameApi, GmIr, GmMob, GmOnline, GmOnlinePagina, GmSkills, GmSkinPegou, GmSkinSet, Guild, GuildApplication, GuildListItem, GuildRulesWire, GuildVault, GuildVote, Hotbar, HuntBoard, HuntEstimate, HuntStart, ProtegidaMenu, ItemCatalog, MobResumo,   NpcShop, Pack, PackItem, Profile, Shard, OficinaFeito,
  OficinaMenu, TailorMenu, FletchMenu, Craft,
  HouseCatalogo, HouseBuy, MinhaCasa, CasaErguida, CasaPlaca, CasaItens, CasaItensMove, OficiosRegras, OficioOnde, FerramentaDaCidade, Banco, BancoMove,
  MercadoCatalogo, MercadoItem, MercadoMeus, MercadoOferta, EventoAgenda, EventoAtual, TabelaCombate, Chefe, AltarTroca,
  LojaVitrine, LojaCompra, LojaPedido, SuporteChamado,
  RankingTabela, RankingMinhas, RankingRegras, WikiDados, Correio, CorreioResumo, CorreioRecebido, Indicacao, ParceiroSessao, ParceiroEntrada, ParceiroPainel, ParceiroLinks,
  VendaJanela, VendaResultado, RecompraResultado, Inspecao, TrajeDto, SepararResultado
} from './types';
import { ApiError, CONFIRMAR_EMAIL, DesafioDoisFatores, type CodigoDoisFatores } from './types';
import { chaveDoErroSemJson } from './erroSemJson';
import { lerHotbar } from '../data/magias';
import { lerCombatIdle, type CombatIdleConfig } from '../data/combateIdle';
import { existe, idiomaAtual, numero, t } from '../i18n/idioma';

/** Só `namespace.chave` entra: código do servidor não vira chave arbitrária. */
const CHAVE_DE_ERRO = /^[a-z][a-zA-Z0-9]*\.[a-zA-Z0-9]+$/;

/*
  Cliente da API .NET.

  A sessão viaja em cookie httpOnly emitido pelo servidor — por isso
  `credentials: 'include'` e nenhum token guardado em JavaScript. Token em
  localStorage é o alvo preferido de XSS; cookie httpOnly + SameSite o script
  da página não consegue ler. Em troca, mutações precisam de proteção CSRF:
  o servidor manda um token no corpo do login e o front devolve em cada
  requisição que muda estado (o cabeçalho abaixo).

  Em desenvolvimento o Vite faz proxy de /api para o backend no host, então a
  chamada é same-origin e não há CORS envolvido.
*/

let catalogOnce: Promise<ItemCatalog> | null = null;
let guildRulesOnce: Promise<GuildRulesWire> | null = null;

const BASE = '/api';

/** O prazo do VIP chega em ms, ou como data ISO se o servidor ainda mandar texto. */
function instanteVip(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v > 0 ? v : 0;
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 1_000_000_000_000) return n;
    const t = Date.parse(v);
    if (!Number.isNaN(t)) return t;
  }
  return 0;
}

const SHARD_HEADER = 'X-UoIdle-Shard';
const SHARD_PADRAO = 'hellfire';
let shardAtual = SHARD_PADRAO;

export function setShardHeader(id: string | null | undefined): void {
  shardAtual = id?.trim() || SHARD_PADRAO;
}

export function shardHeaderValue(): string {
  return shardAtual;
}

let csrfToken: string | null = null;
/** O CSRF da sessão do site dos parceiros (outra sessão, outro cookie: techspec-parceiros §03). */
let csrfParceiro: string | null = null;

function guardarParceiro(e: ParceiroEntrada): ParceiroEntrada {
  if (e.sessao) csrfParceiro = e.sessao.csrfToken;
  return e;
}

async function request<T>(path: string, init: RequestInit = {}, csrf: string | null = csrfToken): Promise<T> {
  const method = init.method ?? 'GET';
  const mutates = method !== 'GET' && method !== 'HEAD';

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        [SHARD_HEADER]: shardAtual,
        ...(init.body ? { 'Content-Type': 'application/json' } : null),
        ...(mutates && csrf ? { 'X-CSRF-Token': csrf } : null),
        ...init.headers
      }
    });
  } catch {
    throw new ApiError(t('comum.semRede'), 0);
  }

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    // Sem JSON nosso (página da Cloudflare, por exemplo), a mensagem sai do status.
    const fallback = t(chaveDoErroSemJson(res.status));
    let message = fallback;
    let code: string | undefined;
    try {
      // O servidor novo manda `{ code, args }` e o cliente traduz; o antigo
      // manda `{ message }` em português. Ler os dois deixa a virada do
      // servidor acontecer sozinha, sem dia da bandeira.
      const body = (await res.json()) as { code?: unknown; args?: unknown; message?: unknown };
      if (typeof body.code === 'string' && body.code.length > 0 && body.code.length <= 80)
        code = body.code;
      if (typeof body.code === 'string' && CHAVE_DE_ERRO.test(body.code) && existe(body.code)) {
        const args = Array.isArray(body.args) ? body.args.map(String) : [];
        const n = args[0] ?? '';
        message = t(body.code, { nome: args[0] ?? '', n: formatarArgDeErro(n) });
      } else if (typeof body.message === 'string' && body.message.length <= 200) {
        message = body.message;
      }
      const preco = (body as { preco?: unknown }).preco;
      if (typeof preco === 'number' && code === 'PrecoMudou') message = `${message} (${preco})`;
    } catch { /* corpo não-JSON: fica a mensagem genérica */ }
    throw new ApiError(message, res.status, code);
  }

  return (await res.json()) as T;
}

async function requestMundo<T>(path: string): Promise<{ body: T; etag: string }> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        [SHARD_HEADER]: shardAtual
      }
    });
  } catch {
    throw new ApiError(t('comum.semRede'), 0);
  }
  if (!res.ok) {
    throw new ApiError(t(chaveDoErroSemJson(res.status)), res.status);
  }
  const etag = res.headers.get('X-Mundo-Etag') ?? '';
  return { body: (await res.json()) as T, etag };
}

type RawNpcMundo = {
  id: string; nome: string; titulo: string; x: number; y: number; dir: number;
  aparencia?: {
    corpo?: string; roupa?: string; pele?: number; visual?: string;
    cabelo?: string; cabeloCor?: number; barba?: string; barbaCor?: number; mao?: string; esquerda?: string;
  };
  temLoja?: boolean; alcance?: number; temCorretor?: boolean; temBanco?: boolean;
};

type RawPortalMundo = {
  id: string; x: number; y: number; destinoMapa: string; destX: number; destY: number;
  efeito?: string; rotulo?: string;
};

type RawManequimMundo = {
  id: string; x: number; y: number; variante?: string;
  dificuldadeArma?: number; dificuldadeMagia?: number; alcance?: number; rotulo?: string;
  uso?: string; defesa?: number; resistenciaMagia?: number;
};

type AuthOut = { account: Account; csrfToken: string };

export function createHttpApi(): GameApi {
  return {
    async signIn(credentials: Credentials): Promise<Account> {
      const out = await request<AuthOut | { etapa: string; desafio: string }>('/auth/sign-in', {
        method: 'POST',
        body: JSON.stringify(credentials)
      });
      // Conta da equipe: a senha conferiu e falta o código do app (techspec-console-2fa-e-cloudflare-access §03.4).
      if ('desafio' in out) throw new DesafioDoisFatores(out.desafio);
      csrfToken = out.csrfToken;
      return out.account;
    },

    async signIn2fa(body: CodigoDoisFatores): Promise<Account> {
      const out = await request<AuthOut>('/auth/2fa', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      csrfToken = out.csrfToken;
      return out.account;
    },

    async signUp(credentials: Credentials): Promise<Account> {
      const out = await request<AuthOut | { code?: string }>('/auth/sign-up', {
        method: 'POST',
        body: JSON.stringify({ ...credentials, idioma: idiomaAtual() })
      });
      // 202: a conta nasceu, mas a sessão só abre com o código do e-mail (techspec-confirmacao-email).
      if (!('account' in out)) throw new ApiError(t(CONFIRMAR_EMAIL), 202, CONFIRMAR_EMAIL);
      csrfToken = out.csrfToken;
      return out.account;
    },

    async confirmarEmail(body: CodigoEmail): Promise<Account> {
      const out = await request<AuthOut>('/auth/confirmar-email', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      csrfToken = out.csrfToken;
      return out.account;
    },

    async reenviarCodigo(email: string): Promise<void> {
      await request<void>('/auth/confirmar-email/reenviar', {
        method: 'POST',
        body: JSON.stringify({ email, idioma: idiomaAtual() })
      });
    },

    async esqueciSenha(email: string): Promise<void> {
      await request<void>('/auth/esqueci-senha', {
        method: 'POST',
        body: JSON.stringify({ email, idioma: idiomaAtual() })
      });
    },

    async redefinirSenha(body: RedefinirSenha): Promise<Account> {
      const out = await request<AuthOut>('/auth/redefinir-senha', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      csrfToken = out.csrfToken;
      return out.account;
    },

    async signInGoogle(body: CredencialGoogle): Promise<Account> {
      const out = await request<AuthOut>('/auth/google', {
        method: 'POST',
        body: JSON.stringify(body)
      });
      csrfToken = out.csrfToken;
      return out.account;
    },

    estadoAcesso: () => request<AcessoEstado>('/acesso/estado'),

    inscreverAcesso: body => request<{ message: string }>('/acesso/inscricoes', {
      method: 'POST', body: JSON.stringify(body)
    }),

    confirmarAcesso: token => request<{ fase: string | null }>('/acesso/confirmar', {
      method: 'POST', body: JSON.stringify({ token })
    }),

    sairDaLista: token => request<{ ok: boolean }>('/acesso/sair', {
      method: 'POST', body: JSON.stringify({ token })
    }),

    founderEstado: () => request<FounderEstado>('/founders/estado'),

    founderCodigo: body => request<{ ok: boolean }>('/founders/codigo', {
      method: 'POST', body: JSON.stringify(body)
    }),

    founderConferir: body => request<FounderConferido>('/founders/codigo/conferir', {
      method: 'POST', body: JSON.stringify(body)
    }),

    founderPedir: body => request<FounderPedido>('/founders/pedidos', {
      method: 'POST', body: JSON.stringify(body)
    }),

    // O segredo do pedido vai no cabeçalho, nunca na URL (não cai em log de proxy nem no histórico).
    founderPedido: (id, segredo) => request<FounderPedido>(`/founders/pedidos/${encodeURIComponent(id)}`, {
      cache: 'no-store', headers: { 'X-Pedido-Segredo': segredo }
    }),

    founderCancelar: (id, segredo) => request<FounderPedido>(`/founders/pedidos/${encodeURIComponent(id)}/cancelar`, {
      method: 'POST', headers: { 'X-Pedido-Segredo': segredo }
    }),

    reenviarConvite: email => request<{ message: string }>('/acesso/convites/reenviar', {
      method: 'POST', body: JSON.stringify({ email })
    }),

    async me(): Promise<Account> {
      const out = await request<AuthOut>('/auth/me');
      csrfToken = out.csrfToken;
      return out.account;
    },

    gravarIdioma: idioma => request<void>('/auth/idioma', {
      method: 'PUT', body: JSON.stringify({ idioma })
    }),

    async signOut(): Promise<void> {
      await request<void>('/auth/sign-out', { method: 'POST' });
      csrfToken = null;
    },

    listShards: () => request<readonly Shard[]>('/shards'),

    listCharacters: (shardId: string) =>
      request<readonly CharacterSummary[]>(`/shards/${encodeURIComponent(shardId)}/characters`),

    creationCatalog: () => request<CreationCatalog>('/creation/catalog'),

    async createCharacter(shardId, draft, idempotencyKey) {
      const out = await request<{ character: CharacterSummary }>(
        `/shards/${encodeURIComponent(shardId)}/characters`,
        {
          method: 'POST',
          body: JSON.stringify(draft),
          headers: { 'Idempotency-Key': idempotencyKey }
        }
      );
      return out.character;
    },

    getCharacter: (shardId, id) =>
      request<CharacterDetail>(`/shards/${encodeURIComponent(shardId)}/characters/${encodeURIComponent(id)}`),

    updateAppearance: (shardId, id, patch) =>
      request<Appearance>(`/shards/${encodeURIComponent(shardId)}/characters/${encodeURIComponent(id)}/appearance`, {
        method: 'PATCH',
        body: JSON.stringify(patch)
      }),

    deleteCharacter: (shardId, id, name) =>
      request<void>(`/shards/${encodeURIComponent(shardId)}/characters/${encodeURIComponent(id)}/delete`, {
        method: 'POST',
        body: JSON.stringify({ name })
      }),

    concluirTutorial: (shardId, id) =>
      request<void>(`/shards/${encodeURIComponent(shardId)}/characters/${encodeURIComponent(id)}/tutorial`, {
        method: 'POST'
      }),

    async listFriends() {
      const out = await request<{ friends: Friend[] }>('/friends');
      return out.friends;
    },

    lookupFriend: (name) =>
      request<FriendLookup>(`/friends/lookup?name=${encodeURIComponent(name)}`),

    addFriend: (name) =>
      request<Friend>('/friends', { method: 'POST', body: JSON.stringify({ name }) }),

    removeFriend: (characterId) =>
      request<void>(`/friends/${encodeURIComponent(characterId)}`, { method: 'DELETE' }),

    reportPlayer: (name, reason) =>
      request<{ id: string }>('/reports', {
        method: 'POST',
        body: JSON.stringify({ name, reason })
      }),

    suporteAbrir: body =>
      request<{ numero: number }>('/suporte/chamados', { method: 'POST', body: JSON.stringify(body) }),
    suporteMeus: async () =>
      (await request<{ chamados: SuporteChamado[] }>('/suporte/chamados')).chamados,
    suporteResumo: () => request<{ precisaDeVoce: number }>('/suporte/resumo'),
    suporteResponder: async (numero, texto) => {
      await request<{ ok: boolean }>(`/suporte/chamados/${encodeURIComponent(String(numero))}/resposta`, {
        method: 'POST',
        body: JSON.stringify({ texto })
      });
    },

    rankingTabela: (tipo, periodo, ordem, busca) => {
      const q = new URLSearchParams({ periodo, ordem });
      if (busca.trim()) q.set('busca', busca.trim());
      return request<RankingTabela>(`/rankings/tabela/${encodeURIComponent(tipo)}?${q.toString()}`);
    },
    rankingMinhas: () => request<RankingMinhas>('/rankings/minhas'),
    rankingRegras: () => request<RankingRegras>('/rankings/regras'),
    correio: () => request<Correio>(`/correio?idioma=${encodeURIComponent(idiomaAtual())}`),
    correioResumo: () => request<CorreioResumo>('/correio/resumo'),
    topidleEstado: () => request<import('./types').TopIdleEstado>('/topidle/estado'),
    correioLer: async id => { await request<void>(`/correio/${encodeURIComponent(id)}/ler`, { method: 'POST', body: '{}' }); },
    correioReceber: (id, anexo) => request<CorreioRecebido>(
      `/correio/${encodeURIComponent(id)}/receber?idioma=${encodeURIComponent(idiomaAtual())}`,
      { method: 'POST', body: JSON.stringify(anexo ? { anexo } : {}) }),
    correioApagar: async id => { await request<void>(`/correio/${encodeURIComponent(id)}/apagar`, { method: 'POST', body: '{}' }); },
    correioApagarLidas: async () => (await request<{ apagadas: number }>('/correio/apagar-lidas', { method: 'POST', body: '{}' })).apagadas,

    indicacoes: () => request<Indicacao>('/indicacoes'),
    indicacoesTrocarCodigo: codigo => request<Indicacao>('/indicacoes/codigo', { method: 'PUT', body: JSON.stringify({ codigo }) }),
    parceiroEu: async () => {
      try {
        const s = await request<ParceiroSessao>('/parceiros/auth/eu');
        csrfParceiro = s.csrfToken;
        return s;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) { csrfParceiro = null; return null; }
        throw e;
      }
    },
    parceiroEntrar: async (email, senha) => guardarParceiro(await request<ParceiroEntrada>('/parceiros/auth/entrar',
      { method: 'POST', body: JSON.stringify({ email, senha }) }, null)),
    parceiroDoisFatores: async (desafio, codigo) => guardarParceiro(await request<ParceiroEntrada>('/parceiros/auth/2fa',
      { method: 'POST', body: JSON.stringify({ desafio, codigo }) }, null)),
    parceiroTrocarSenha: async (atual, nova) => guardarParceiro(await request<ParceiroEntrada>('/parceiros/auth/senha',
      { method: 'POST', body: JSON.stringify({ atual, nova }) }, csrfParceiro)),
    parceiroSair: async () => {
      try { await request<void>('/parceiros/auth/sair', { method: 'POST' }, csrfParceiro); } finally { csrfParceiro = null; }
    },
    parceiroPainel: dias => request<ParceiroPainel>(`/parceiros/painel?dias=${dias}`, {}, csrfParceiro),
    parceiroLinks: () => request<ParceiroLinks>('/parceiros/links', {}, csrfParceiro),
    parceiroCriarLink: (canal, nome) =>
      request<ParceiroLinks>('/parceiros/links', { method: 'POST', body: JSON.stringify({ canal, nome }) }, csrfParceiro),
    origemVisita: async visita => {
      try {
        return (await request<{ convite: string | null }>('/origem/visita', { method: 'POST', body: JSON.stringify(visita) })).convite ?? null;
      } catch {
        return null;
      }
    },

    wikiDados: () => request<WikiDados>('/wiki/v1'),

    itemCatalog: () => {
      catalogOnce ??= request<ItemCatalog>('/items/catalog').catch(err => {
        catalogOnce = null;
        throw err;
      });
      return catalogOnce;
    },

    getPack: () => request<Pack>('/pack'),

    async loja(etag) {
      const r = await requestMundo<LojaVitrine>(`/mundo/${encodeURIComponent(etag)}/loja`);
      return r.body;
    },

    comprarLoja: body =>
      request<LojaCompra>('/loja/comprar', { method: 'POST', body: JSON.stringify(body) }),

    pedidosLoja: () => request<readonly LojaPedido[]>('/loja/pedidos'),

    lojaOuro: () => request<import('./types').LojaOuro>('/loja/ouro'),
    pedirOuro: body =>
      request<import('./types').PagamentoOuro>('/loja/ouro/pedidos', { method: 'POST', body: JSON.stringify(body) }),
    pedidoOuro: id => request<import('./types').PagamentoOuro>(`/loja/ouro/pedidos/${encodeURIComponent(id)}`),
    historicoOuro: () => request<readonly import('./types').HistoricoOuro[]>('/loja/ouro/historico'),
    cancelarOuro: id =>
      request<import('./types').PagamentoOuro>(`/loja/ouro/pedidos/${encodeURIComponent(id)}/cancelar`, { method: 'POST' }),

    vip: async () => {
      const v = await request<import('./types').VipConta>('/vip');
      return { ...v, ate: instanteVip(v.ate) };
    },
    vipMostrar: (mostrar: boolean) =>
      request<void>('/vip/mostrar', { method: 'PUT', body: JSON.stringify({ mostrar }) }),

    discordEstado: () => request<import('./types').DiscordEstado>('/discord/estado'),
    discordVincular: () => request<{ url: string }>('/discord/vincular', { method: 'POST' }),
    discordAtualizar: () => request<import('./types').DiscordEstado>('/discord/atualizar', { method: 'POST' }),
    discordDesvincular: () => request<void>('/discord/desvincular', { method: 'POST' }),
    discordLoginAtivo: () => request<{ ativo: boolean }>('/discord/login'),
    discordEntrar: body => request<{ url: string }>('/discord/entrar', { method: 'POST', body: JSON.stringify(body) }),


    npcShop: (npcId) => request<NpcShop>(`/npcs/${encodeURIComponent(npcId)}/loja`),

    vendas: () => request<VendaJanela>('/vendas'),

    vender: body => request<VendaResultado>('/vendas', { method: 'POST', body: JSON.stringify(body) }),

    recomprar: id => request<RecompraResultado>(`/vendas/recompra/${encodeURIComponent(id)}`, { method: 'POST' }),
    recomprarVarias: ids => request<RecompraResultado>('/vendas/recompra', { method: 'POST', body: JSON.stringify({ ids }) }),

    travarVenda: (defId, travado) =>
      request<{ defId: string; travado: boolean }>(`/vendas/travas/${encodeURIComponent(defId)}`, {
        method: 'PUT',
        body: JSON.stringify({ travado })
      }),

    cidadeLoja: () => request<CidadeLoja>('/cidade/loja'),

    comprarCidade: itens =>
      request<Pack>('/cidade/comprar', {
        method: 'POST',
        body: JSON.stringify({ itens })
      }),

    buyNpc: (npcId, itens) =>
      request<Pack>(`/npcs/${encodeURIComponent(npcId)}/comprar`, {
        method: 'POST',
        body: JSON.stringify({ itens })
      }),

    getBanco: (npcId) => request<Banco>(`/banco/${encodeURIComponent(npcId)}`),

    guardarBanco: (npcId, body) =>
      request<BancoMove>(`/banco/${encodeURIComponent(npcId)}/guardar`, {
        method: 'POST',
        body: JSON.stringify(body)
      }),

    sacarBanco: (npcId, body) =>
      request<BancoMove>(`/banco/${encodeURIComponent(npcId)}/sacar`, {
        method: 'POST',
        body: JSON.stringify(body)
      }),

    depositarMoeda: (npcId, body) =>
      request<BancoMove>(`/banco/${encodeURIComponent(npcId)}/moeda/depositar`, {
        method: 'POST',
        body: JSON.stringify(body)
      }),

    sacarMoeda: (npcId, body) =>
      request<BancoMove>(`/banco/${encodeURIComponent(npcId)}/moeda/sacar`, {
        method: 'POST',
        body: JSON.stringify(body)
      }),

    listarModelos: (npcId) =>
      request<HouseCatalogo>(`/casas/${encodeURIComponent(npcId)}/modelos`),

    comprarCasa: (npcId, modeloId) =>
      request<HouseBuy>(`/casas/${encodeURIComponent(npcId)}/comprar`, {
        method: 'POST',
        body: JSON.stringify({ modeloId })
      }),

    minhaCasa: () => request<MinhaCasa | null>('/casas/minha'),
    casasErguidas: (mapaId: string) => request<CasaErguida[]>(`/casas/erguidas/${encodeURIComponent(mapaId)}`),
    devolverCasa: () => request<void>('/casas/minha/devolver', { method: 'POST', body: '{}' }),
    casaItens: houseId => request<CasaItens>(`/casas/${encodeURIComponent(houseId)}/itens`),
    prenderNaCasa: (houseId, body) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/prender`, { method: 'POST', body: JSON.stringify(body) }),
    soltarDaCasa: (houseId, id) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/presos/${encodeURIComponent(id)}/soltar`, { method: 'POST', body: '{}' }),
    moverNaCasa: (houseId, id, body) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/itens/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
    oficiosRegras: () => request<OficiosRegras>('/casa/oficios/regras'),
    oficioOnde: oficio => request<OficioOnde>(`/oficio/${encodeURIComponent(oficio)}/onde`),
    ferramentasDaCidade: mapa => request<readonly FerramentaDaCidade[]>(`/oficio/ferramentas/${encodeURIComponent(mapa)}`),
    porBauNaCasa: (houseId, ponto) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/baus`, { method: 'POST', body: JSON.stringify(ponto) }),
    girarBauDaCasa: (houseId, bauId) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/baus/${encodeURIComponent(bauId)}/girar`, { method: 'POST', body: '{}' }),
    recolherBauDaCasa: (houseId, bauId) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/baus/${encodeURIComponent(bauId)}/recolher`, { method: 'POST', body: '{}' }),
    guardarNoBau: (houseId, bauId, body) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/baus/${encodeURIComponent(bauId)}/guardar`, { method: 'POST', body: JSON.stringify(body) }),
    tirarDoBau: (houseId, bauId, body) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/baus/${encodeURIComponent(bauId)}/tirar`, { method: 'POST', body: JSON.stringify(body) }),
    configBauDaCasa: (houseId, bauId, body) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/baus/${encodeURIComponent(bauId)}/config`, { method: 'POST', body: JSON.stringify(body) }),
    casaAberta: aberta => request<MinhaCasa>('/casas/minha/aberta', { method: 'POST', body: JSON.stringify({ aberta }) }),
    moverBauDaCasa: (houseId, bauId, body) => request<CasaItensMove>(`/casas/${encodeURIComponent(houseId)}/baus/${encodeURIComponent(bauId)}`, { method: 'PATCH', body: JSON.stringify(body) }),
    placaDaCasa: houseId => request<CasaPlaca>(`/casas/${encodeURIComponent(houseId)}/placa`),
    nomeDaCasa: nome => request<MinhaCasa>('/casas/minha/nome', { method: 'POST', body: JSON.stringify({ nome }) }),

    convidar: (nome, papel) =>
      request<MinhaCasa>('/casas/minha/convidados', {
        method: 'POST',
        body: JSON.stringify(papel ? { nome, papel } : { nome })
      }),

    desconvidar: (nome) =>
      request<MinhaCasa>(`/casas/minha/convidados/${encodeURIComponent(nome)}`, {
        method: 'DELETE'
      }),

    oficinaMenu: (oficio) => request<OficinaMenu>(`/crafting/oficina/${oficio}`),

    oficinaFazer: (oficio, defId, material) =>
      request<OficinaFeito>(`/crafting/oficina/${oficio}`, {
        method: 'POST',
        body: JSON.stringify({ defId, material })
      }),


    tailorMenu: () => request<TailorMenu>('/crafting/tailor'),

    sew: (defId) =>
      request<Craft>('/crafting/tailor/sew', {
        method: 'POST',
        body: JSON.stringify({ defId })
      }),

    fletchMenu: () => request<FletchMenu>('/crafting/fletch'),

    carve: (defId) =>
      request<Craft>('/crafting/fletch/carve', {
        method: 'POST',
        body: JSON.stringify({ defId })
      }),

    buyItem: (defId, qty, characterId) =>
      request<PackItem>('/market/buy', {
        method: 'POST',
        body: JSON.stringify({ defId, qty, characterId })
      }),

    sellItem: (itemId, qty) =>
      request<void>('/market/sell', {
        method: 'POST',
        body: JSON.stringify({ itemId, qty })
      }),

    mercadoCatalogo: (query = {}) => {
      const q = new URLSearchParams();
      if (query.q) q.set('q', query.q);
      if (query.grupo) q.set('grupo', query.grupo);
      if (query.soOferta) q.set('soOferta', 'true');
      if (query.ordem) q.set('ordem', query.ordem);
      const qs = q.toString();
      return request<MercadoCatalogo>(`/mercado/catalogo${qs ? `?${qs}` : ''}`);
    },

    mercadoItem: (defId, query = {}) => {
      const q = new URLSearchParams();
      if (query.material) q.set('material', query.material);
      if (query.soImediata) q.set('soImediata', 'true');
      if (query.soPago) q.set('soPago', 'true');
      if (query.ordem) q.set('ordem', query.ordem);
      const qs = q.toString();
      return request<MercadoItem>(`/mercado/item/${encodeURIComponent(defId)}${qs ? `?${qs}` : ''}`);
    },

    mercadoOferta: (lotId) => request<MercadoOferta>(`/mercado/oferta/${encodeURIComponent(lotId)}`),

    mercadoMeus: () => request<MercadoMeus>('/mercado/meus'),

    mercadoAnunciar: (body) =>
      request<{ id: string }>('/mercado/anunciar', {
        method: 'POST',
        body: JSON.stringify(body)
      }),

    mercadoLance: (lotId, teto) =>
      request<MercadoOferta>('/mercado/lance', {
        method: 'POST',
        body: JSON.stringify({ lotId, teto })
      }),

    mercadoComprarJa: (lotId) =>
      request<{ id: string }>('/mercado/comprar-ja', {
        method: 'POST',
        body: JSON.stringify({ lotId })
      }),

    mercadoCancelar: (lotId) =>
      request<void>('/mercado/cancelar', {
        method: 'POST',
        body: JSON.stringify({ lotId })
      }),

    mercadoRetirar: (lotId) =>
      request<void>('/mercado/retirar', {
        method: 'POST',
        body: JSON.stringify({ lotId })
      }),

    equipItem: (itemId) =>
      request<Pack>('/pack/equip', {
        method: 'POST',
        body: JSON.stringify({ itemId })
      }),

    unequipItem: (itemId) =>
      request<Pack>('/pack/unequip', {
        method: 'POST',
        body: JSON.stringify({ itemId })
      }),

    getTraje: () => request<TrajeDto>('/traje'),

    salvarTraje: (doc) =>
      request<TrajeDto>('/traje', {
        method: 'PUT',
        body: JSON.stringify(doc)
      }),

    guardarTinta: (itemId) =>
      request<TrajeDto>('/traje/tintas/guardar', {
        method: 'POST',
        body: JSON.stringify({ itemId })
      }),

    retirarTinta: (cor) =>
      request<TrajeDto>(`/traje/tintas/${encodeURIComponent(cor)}/retirar`, { method: 'POST' }),

    guardarSkin: (itemId) =>
      request<TrajeDto>('/traje/skins/guardar', {
        method: 'POST',
        body: JSON.stringify({ itemId })
      }),

    extrairSkin: (peca) =>
      request<TrajeDto>(`/traje/skins/${encodeURIComponent(peca)}/extrair`, { method: 'POST' }),

    marcarSkinVista: (peca) =>
      request<void>(`/traje/exclusivas/${encodeURIComponent(peca)}/vista`, { method: 'POST' }),

    dropItem: (itemId) =>
      request<Pack>('/pack/drop', {
        method: 'POST',
        body: JSON.stringify({ itemId })
      }),

    separarItem: (itemId, qty) =>
      request<SepararResultado>('/pack/separar', { method: 'POST', body: JSON.stringify({ itemId, qty }) }),

    juntarItem: (itemId, destinoId) =>
      request<Pack>('/pack/juntar', { method: 'POST', body: JSON.stringify({ itemId, destinoId }) }),

    getProfile: () => request<Profile>('/profile'),
    inspecionar: (nome: string) => request<Inspecao>(`/inspecionar?nome=${encodeURIComponent(nome)}`),

    lockSkill: (skillId, lock) =>
      request<Profile>('/profile/skill-lock', {
        method: 'POST',
        body: JSON.stringify({ skillId, lock })
      }),

    limparProgresso: () => request<Profile>('/profile/progresso/limpar', { method: 'POST' }),

    async getHotbar(): Promise<Hotbar> {
      const raw = await request<{ slots?: unknown }>('/hotbar');
      return lerHotbar(raw.slots) as Hotbar;
    },

    setHotbar: (slots) =>
      request<void>('/hotbar', {
        method: 'POST',
        body: JSON.stringify({ slots })
      }),

    async getCombatIdle(): Promise<CombatIdleConfig> {
      return lerCombatIdle(await request<unknown>('/combat-idle'));
    },

    // 204 no POST: o cliente já tem o que mandou, e quem quiser o resultado da
    // limpeza do servidor faz o GET.
    setCombatIdle: (config) =>
      request<void>('/combat-idle', {
        method: 'POST',
        body: JSON.stringify(config)
      }),

    async getCacadaEspalhada(): Promise<boolean> {
      const raw = await request<{ espalhar?: unknown }>('/combat-idle');
      return raw?.espalhar === true;
    },

    setCacadaEspalhada: (ligado) =>
      request<void>('/combat-idle/espalhar', {
        method: 'POST',
        body: JSON.stringify({ ligado })
      }),

    eventoAtual: () => request<EventoAtual>('/evento/atual'),
    eventoAgenda: () => request<EventoAgenda>('/evento/agenda'),
    eventoInscrever: () => request<void>('/evento/inscrever', { method: 'POST' }),
    eventoDesistir: () => request<void>('/evento/desistir', { method: 'POST' }),
    eventoAbrir: (body) => request<void>('/evento/abrir', { method: 'POST', body: JSON.stringify(body) }),
    eventoCancelar: () => request<void>('/evento/cancelar', { method: 'POST' }),
    eventoChefe: () => request<Chefe>('/evento/chefe'),
    eventoChefeIniciar: async body => { await request<unknown>('/evento/chefe/iniciar', { method: 'POST', body: JSON.stringify(body) }); },
    eventoChefeEncerrar: () => request<void>('/evento/chefe/encerrar', { method: 'POST' }),
    eventoPentagrama: () => request<import('./evento').Penta>('/evento/pentagrama'),
    eventoPentagramaIniciar: async cor => {
      await request<unknown>('/evento/pentagrama/iniciar', { method: 'POST', body: JSON.stringify({ cor: cor ?? null }) });
    },
    eventoPentagramaEncerrar: () => request<void>('/evento/pentagrama/encerrar', { method: 'POST' }),
    eventoAltarTrocar: (oferta, versao) =>
      request<AltarTroca>(`/evento/chefe/altar/${encodeURIComponent(oferta)}/trocar`, { method: 'POST', body: JSON.stringify({ versao }) }),

    listGmOnline: (q, take, sala) => {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (take) qs.set('take', String(take));
      if (sala) qs.set('sala', 'true');
      const suffix = qs.toString();
      return request<GmOnlinePagina>(`/gm/online${suffix ? `?${suffix}` : ''}`);
    },

    gmOculto: (ligado) =>
      request<{ ligado: boolean }>('/gm/oculto', {
        method: 'POST',
        body: JSON.stringify({ ligado })
      }),

    gmSkins: () => request<readonly GmSkinSet[]>('/gm/skins'),

    gmPegarSkin: (set) =>
      request<GmSkinPegou>('/gm/skins/pegar', {
        method: 'POST',
        body: JSON.stringify({ set })
      }),

    gmHora: (hora, minuto) =>
      request<{ minuto: number; msPorMinuto: number }>('/gm/hora', {
        method: 'POST',
        body: JSON.stringify({ hora, minuto })
      }),

    gmIr: (characterId) =>
      request<GmIr>('/gm/ir', {
        method: 'POST',
        body: JSON.stringify({ characterId })
      }),

    gmKill: (characterId) =>
      request<GmOnline>('/gm/kill', {
        method: 'POST',
        body: JSON.stringify({ characterId })
      }),

    gmRevive: (characterId) =>
      request<GmOnline>('/gm/revive', {
        method: 'POST',
        body: JSON.stringify({ characterId })
      }),

    gmNotoriety: (characterId, notoriety) =>
      request<GmOnline>('/gm/notoriety', {
        method: 'POST',
        body: JSON.stringify({ characterId, notoriety })
      }),

    gmTeleport: (characterId, x, y) =>
      request<GmOnline>('/gm/teleport', {
        method: 'POST',
        body: JSON.stringify({ characterId, x, y })
      }),

    gmCity: (characterId, cityId) =>
      request<GmOnline>('/gm/city', {
        method: 'POST',
        body: JSON.stringify({ characterId, cityId })
      }),

    gmBank: (characterId, prata, ouro) =>
      request<GmOnline>('/gm/bank', {
        method: 'POST',
        body: JSON.stringify({ characterId, prata, ouro })
      }),

    gmSkills: (characterId) =>
      request<GmSkills>(`/gm/skills?characterId=${encodeURIComponent(characterId)}`),

    gmSetSkills: (characterId, skills, skillCap, atributos) =>
      request<GmSkills>('/gm/skills', {
        method: 'POST',
        body: JSON.stringify({ characterId, skills, skillCap, ...(atributos ?? {}) })
      }),

    listMobCatalog: () => request<readonly MobResumo[]>('/mobs'),

    listGmMobs: () => request<readonly GmMob[]>('/gm/mobs'),

    gmSpawnMob: (defId, x, y, range) =>
      request<GmMob>('/gm/mob', {
        method: 'POST',
        body: JSON.stringify({ defId, x, y, range })
      }),

    gmRemoveMob: (shortId) =>
      request<GmMob>('/gm/mob/remove', {
        method: 'POST',
        body: JSON.stringify({ shortId })
      }),

    getGuildMe: () => request<Guild | null>('/guilds/me'),
    getGuildRules: () => {
      guildRulesOnce ??= request<GuildRulesWire>('/guilds/rules');
      return guildRulesOnce;
    },
    listGuilds: () => request<readonly GuildListItem[]>('/guilds'),
    createGuild: (name, tag) =>
      request<Guild>('/guilds', { method: 'POST', body: JSON.stringify({ name, tag }) }),
    leaveGuild: () => request<void>('/guilds/leave', { method: 'POST' }),
    inviteGuild: (name) =>
      request<void>('/guilds/invite', { method: 'POST', body: JSON.stringify({ name }) }),
    acceptGuild: () => request<Guild>('/guilds/accept', { method: 'POST' }),
    declineGuild: () => request<void>('/guilds/decline', { method: 'POST' }),
    applyGuild: (id) => request<void>(`/guilds/${encodeURIComponent(id)}/apply`, { method: 'POST' }),
    listGuildApplications: () => request<readonly GuildApplication[]>('/guilds/me/applications'),
    acceptGuildApplication: (id) =>
      request<void>(`/guilds/applications/${encodeURIComponent(id)}/accept`, { method: 'POST' }),
    refuseGuildApplication: (id) =>
      request<void>(`/guilds/applications/${encodeURIComponent(id)}/refuse`, { method: 'POST' }),
    setGuildRank: (name, rank) =>
      request<void>('/guilds/rank', { method: 'POST', body: JSON.stringify({ name, rank }) }),
    passGuildLead: (name) =>
      request<void>('/guilds/lead', { method: 'POST', body: JSON.stringify({ name }) }),
    kickGuild: (name) =>
      request<void>('/guilds/kick', { method: 'POST', body: JSON.stringify({ name }) }),
    setGuildTitle: (name, title) =>
      request<void>('/guilds/title', { method: 'POST', body: JSON.stringify({ name, title }) }),
    setGuildMotd: (text) =>
      request<void>('/guilds/motd', { method: 'POST', body: JSON.stringify({ text }) }),
    voteGuild: (name) =>
      request<GuildVote>('/guilds/vote', { method: 'POST', body: JSON.stringify({ name }) }),
    setGuildTithe: (percent) =>
      request<void>('/guilds/tithe', { method: 'POST', body: JSON.stringify({ percent }) }),
    withdrawGuildVault: (amount) =>
      request<GuildVault>('/guilds/vault/withdraw', { method: 'POST', body: JSON.stringify({ amount }) }),
    getGuildVault: () => request<GuildVault>('/guilds/vault'),
    proposeAlliance: (tag) =>
      request<void>('/guilds/alliance/propose', { method: 'POST', body: JSON.stringify({ tag }) }),
    acceptAlliance: () => request<void>('/guilds/alliance/accept', { method: 'POST' }),
    declineAlliance: () => request<void>('/guilds/alliance/decline', { method: 'POST' }),
    leaveAlliance: () => request<void>('/guilds/alliance/leave', { method: 'POST' }),
    proposeWar: (tag, format, killsTo, hours) =>
      request<void>('/guilds/war/propose', { method: 'POST', body: JSON.stringify({ tag, format, killsTo, hours }) }),
    acceptWar: () => request<void>('/guilds/war/accept', { method: 'POST' }),
    declineWar: () => request<void>('/guilds/war/decline', { method: 'POST' }),
    surrenderWar: () => request<void>('/guilds/war/surrender', { method: 'POST' }),
    gmCreateGuild: (characterId, name, tag) =>
      request<GmOnline>('/gm/guild', { method: 'POST', body: JSON.stringify({ characterId, name, tag }) }),
    gmLeaveGuild: (characterId) =>
      request<GmOnline>('/gm/guild/leave', { method: 'POST', body: JSON.stringify({ characterId }) }),
    gmGuildVault: (characterId, amount) =>
      request<GmOnline>('/gm/guild/vault', { method: 'POST', body: JSON.stringify({ characterId, amount }) }),

    getHunts: () => request<HuntBoard>('/hunts'),

    estimateHunt: (body: HuntStart) =>
      request<HuntEstimate>('/hunts/estimate', {
        method: 'POST',
        body: JSON.stringify(body)
      }),

    startHunt: (body: HuntStart) =>
      request<HuntBoard>('/hunts/start', {
        method: 'POST',
        body: JSON.stringify(body)
      }),

    protegidaMenu: () => request<ProtegidaMenu>('/hunts/protegida'),

    cancelHunt: () => request<HuntBoard>('/hunts/cancel', { method: 'POST' }),

    autoHunt: () => request<HuntBoard>('/hunts/auto', { method: 'POST' }),

    manualHunt: () => request<HuntBoard>('/hunts/manual', { method: 'POST' }),

    regraHunt: body => request<HuntBoard>('/hunts/regra', { method: 'POST', body: JSON.stringify(body) }),

    foraVistoHunt: () => request<HuntBoard>('/hunts/fora/visto', { method: 'POST' }),

    getColeta: () => request<ColetaEstado>('/coleta'),

    iniciarColeta: (recurso: 'madeira' | 'minerio') =>
      request<ColetaEstado>('/coleta/iniciar', { method: 'POST', body: JSON.stringify({ recurso }) }),

    cancelarColeta: (destino?: string) =>
      request<ColetaEstado>('/coleta/cancelar', { method: 'POST', body: JSON.stringify({ destino: destino ?? null }) }),

    async tabelaCombate(etag) {
      const r = await requestMundo<TabelaCombate>(`/mundo/${encodeURIComponent(etag)}/combate`);
      return { tabela: r.body, etag: r.etag || etag };
    },

    esquemaMundo: () => request<{ esquemaVersao: number }>('/mundo/esquema'),

    async npcsDoMundo(etag, mapa) {
      const r = await requestMundo<{ npcs: RawNpcMundo[] }>(
        `/mundo/${encodeURIComponent(etag)}/npcs/${encodeURIComponent(mapa)}`);
      return (r.body.npcs ?? []).map(n => ({
        id: n.id,
        nome: n.nome,
        titulo: n.titulo,
        x: n.x | 0,
        y: n.y | 0,
        dir: n.dir | 0,
        corpo: n.aparencia?.corpo ?? '',
        roupa: n.aparencia?.roupa ?? '',
        pele: n.aparencia?.pele ?? 0,
        visual: n.aparencia?.visual ?? '',
        cabelo: n.aparencia?.cabelo ?? '',
        cabeloCor: n.aparencia?.cabeloCor ?? 0,
        barba: n.aparencia?.barba ?? '',
        barbaCor: n.aparencia?.barbaCor ?? 0,
        mao: n.aparencia?.mao ?? '',
        esquerda: n.aparencia?.esquerda ?? '',
        alcance: n.alcance ?? 2,
        temLoja: !!n.temLoja,
        temCorretor: !!n.temCorretor,
        temBanco: !!n.temBanco
      }));
    },

    async manequinsDoMundo(etag, mapa) {
      const r = await requestMundo<{ manequins: RawManequimMundo[] }>(
        `/mundo/${encodeURIComponent(etag)}/manequins/${encodeURIComponent(mapa)}`);
      return (r.body.manequins ?? []).map(m => ({
        id: m.id,
        x: m.x | 0,
        y: m.y | 0,
        variante: m.variante === 'noroeste' ? 'noroeste' as const : 'nordeste' as const,
        dificuldadeArma: typeof m.dificuldadeArma === 'number' ? m.dificuldadeArma : 30,
        dificuldadeMagia: typeof m.dificuldadeMagia === 'number' ? m.dificuldadeMagia : 30,
        alcance: m.alcance ?? 1,
        rotulo: m.rotulo ?? '',
        uso: m.uso === 'combate' ? 'combate' as const : 'treino' as const,
        defesa: typeof m.defesa === 'number' ? m.defesa : 0,
        resistenciaMagia: typeof m.resistenciaMagia === 'number' ? m.resistenciaMagia : 0
      }));
    },

    async viagensDoMundo(etag) {
      const r = await requestMundo<{ destinos: { id: string; mapa: string; x: number; y: number; rotulo: string; tipo?: string; guardas?: boolean; pontos?: { x: number; y: number }[] }[] }>(
        `/mundo/${encodeURIComponent(etag)}/viagens`);
      return (r.body.destinos ?? []).map(v => ({
        id: v.id,
        mapa: v.mapa,
        x: v.x | 0,
        y: v.y | 0,
        rotulo: v.rotulo || v.mapa,
        tipo: v.tipo,
        guardas: v.guardas,
        pontos: (v.pontos ?? []).map(p => ({ x: p.x | 0, y: p.y | 0 }))
      }));
    },

    async ankhsDoMundo(etag, mapa) {
      const r = await requestMundo<{ ankhs: { id: string; x: number; y: number; raio: number; rotulo: string }[] }>(
        `/mundo/${encodeURIComponent(etag)}/ankhs/${encodeURIComponent(mapa)}`);
      return (r.body.ankhs ?? []).map(a => ({ id: a.id, x: a.x | 0, y: a.y | 0, raio: a.raio | 0, rotulo: a.rotulo ?? '' }));
    },

    async portaisDoMundo(etag, mapa) {
      const r = await requestMundo<{ portais: RawPortalMundo[] }>(
        `/mundo/${encodeURIComponent(etag)}/portais/${encodeURIComponent(mapa)}`);
      return (r.body.portais ?? []).map(p => ({
        id: p.id,
        x: p.x | 0,
        y: p.y | 0,
        destinoMapa: p.destinoMapa,
        destX: p.destX | 0,
        destY: p.destY | 0,
        efeito: p.efeito || 'gate-travel-opened',
        rotulo: p.rotulo || p.destinoMapa
      }));
    }
  };
}

function formatarArgDeErro(raw: string): string {
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return raw;
  const n = Number(raw);
  if (raw.includes('.'))
    return n.toLocaleString(idiomaAtual(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return numero(n);
}
